import { CheckIcon } from "lucide-react";

import type { WorkspaceAdminResponse } from "../../shared/schemas";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";

function ToolChoice({
  checked,
  disabled,
  selected,
  tool,
  onChange,
}: {
  checked: boolean;
  disabled: boolean;
  selected: string[];
  tool: WorkspaceAdminResponse["tools"][number];
  onChange: (next: string[]) => void;
}) {
  const inputId = `workspace-tool-choice-${tool.appInstanceId}`;
  return (
    <label
      className={cn(
        "flex min-h-11 cursor-pointer items-center gap-3 rounded-sm border bg-card px-3 py-2.5 text-sm font-medium shadow-[0_1px_0_rgba(0,0,0,0.03)] transition-[border-color,background-color,box-shadow] duration-150 has-[:focus-visible]:shadow-[var(--focus-ring)]",
        checked && "border-primary bg-info-background",
        disabled && "cursor-not-allowed opacity-55",
      )}
      htmlFor={inputId}
    >
      <span
        aria-hidden="true"
        className={cn(
          "grid size-5 shrink-0 place-items-center rounded-sm border border-input bg-background text-transparent",
          checked && "border-primary-deep bg-primary text-primary-foreground",
        )}
      >
        <CheckIcon className="size-3.5" />
      </span>
      <input
        checked={checked}
        className="sr-only"
        disabled={disabled}
        id={inputId}
        type="checkbox"
        onChange={(event) => {
          onChange(
            event.target.checked
              ? [...selected, tool.appInstanceId]
              : selected.filter((appInstanceId) => appInstanceId !== tool.appInstanceId),
          );
        }}
      />
      <span className="min-w-0 truncate">{tool.displayName}</span>
    </label>
  );
}

export function ToolSelection({
  disabled,
  selected,
  tools,
  onChange,
}: {
  disabled: boolean;
  selected: string[];
  tools: WorkspaceAdminResponse["tools"];
  onChange: (next: string[]) => void;
}) {
  const selectedSet = new Set(selected);
  return (
    <fieldset className="grid gap-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <legend className="text-sm font-semibold">Tool access</legend>
          <p className="mt-1 mb-0 text-sm text-muted-foreground">
            {selected.length} of {tools.length} tools selected
          </p>
        </div>
        <div className="flex gap-1">
          <Button
            disabled={disabled || selected.length === tools.length}
            size="sm"
            type="button"
            variant="ghost"
            onClick={() => {
              onChange(tools.map(({ appInstanceId }) => appInstanceId));
            }}
          >
            Select all
          </Button>
          <Button
            disabled={disabled || selected.length === 0}
            size="sm"
            type="button"
            variant="ghost"
            onClick={() => {
              onChange([]);
            }}
          >
            Clear
          </Button>
        </div>
      </div>
      <div className="grid max-h-56 gap-2 overflow-y-auto rounded-lg bg-muted/45 p-2 sm:grid-cols-2">
        {tools.map((tool) => (
          <ToolChoice
            checked={selectedSet.has(tool.appInstanceId)}
            disabled={disabled}
            key={tool.appInstanceId}
            selected={selected}
            tool={tool}
            onChange={onChange}
          />
        ))}
      </div>
    </fieldset>
  );
}
